import React, { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

export default function PartsList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [parts, setParts] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState(searchParams.get('filter') || 'all')
  const [search, setSearch] = useState('')
  const [sortField, setSortField] = useState('description')
  const [sortDirection, setSortDirection] = useState('asc')

  useEffect(() => {
    fetchParts()
  }, [filter, search])

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter)
    if (newFilter === 'all') {
      searchParams.delete('filter')
    } else {
      searchParams.set('filter', newFilter)
    }
    setSearchParams(searchParams)
  }

  const fetchParts = () => {
    setLoading(true)
    let url = '/api/parts'
    const params = new URLSearchParams()

    if (filter === 'needs_reorder') params.append('needs_reorder', 'true')
    if (filter === 'in_stock') params.append('in_stock', 'true')
    if (search) params.append('search', search)

    if (params.toString()) url += '?' + params.toString()

    fetch(url)
      .then(response => response.json())
      .then(data => {
        setParts(data)
        setLoading(false)
      })
      .catch(err => {
        console.error('Error fetching parts:', err)
        setLoading(false)
      })
  }

  const deletePart = (id) => {
    if (!confirm('Are you sure you want to delete this part?')) return

    fetch(`/api/parts/${id}`, {
      method: 'DELETE'
    })
      .then(response => {
        if (response.ok) {
          setParts(parts.filter(part => part.id !== id))
        } else {
          return response.json().then(data => {
            alert(data.errors?.join(', ') || 'Failed to delete part')
          })
        }
      })
      .catch(err => console.error('Error deleting part:', err))
  }

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  }

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const sortedParts = [...parts].sort((a, b) => {
    let aVal = a[sortField]
    let bVal = b[sortField]

    if (aVal === null || aVal === undefined) aVal = ''
    if (bVal === null || bVal === undefined) bVal = ''

    if (sortField === 'price' || sortField === 'quantity_in_stock' || sortField === 'reorder_threshold') {
      aVal = parseFloat(aVal) || 0
      bVal = parseFloat(bVal) || 0
    }

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      aVal = aVal.toLowerCase()
      bVal = bVal.toLowerCase()
    }

    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0
    }
  })

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return (
        <span className="ml-1 text-gray-400">
          <svg className="inline w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path d="M5 10l5-5 5 5H5z"/>
          </svg>
        </span>
      )
    }
    return (
      <span className="ml-1">
        {sortDirection === 'asc' ? (
          <svg className="inline w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path d="M5 10l5-5 5 5H5z"/>
          </svg>
        ) : (
          <svg className="inline w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path d="M15 10l-5 5-5-5h10z"/>
          </svg>
        )}
      </span>
    )
  }

  return (
    <div className="px-4 sm:px-0">
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Parts</h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage inventory parts
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Link
            to="/parts/new"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
          >
            + Add Part
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Status
            </label>
            <div className="flex space-x-2">
              <button
                onClick={() => handleFilterChange('all')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => handleFilterChange('needs_reorder')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filter === 'needs_reorder'
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Needs Reorder
              </button>
              <button
                onClick={() => handleFilterChange('in_stock')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filter === 'in_stock'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                In Stock
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by description or vendor..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>
        </div>
      </div>

      {/* Parts Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">Loading parts...</div>
          </div>
        ) : parts.length === 0 ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">No parts found</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Photo
                  </th>
                  <th
                    onClick={() => handleSort('description')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Description <SortIcon field="description" />
                  </th>
                  <th
                    onClick={() => handleSort('price')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Price <SortIcon field="price" />
                  </th>
                  <th
                    onClick={() => handleSort('quantity_in_stock')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    In Stock <SortIcon field="quantity_in_stock" />
                  </th>
                  <th
                    onClick={() => handleSort('reorder_threshold')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Reorder At <SortIcon field="reorder_threshold" />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th
                    onClick={() => handleSort('vendor_name')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Vendor <SortIcon field="vendor_name" />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedParts.map((part) => (
                  <tr key={part.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {part.photo_thumbnail_url ? (
                        <img
                          src={part.photo_thumbnail_url}
                          alt={part.description}
                          className="h-10 w-10 object-cover rounded"
                        />
                      ) : (
                        <div className="h-10 w-10 bg-gray-200 rounded flex items-center justify-center">
                          <span className="text-gray-400 text-xs">No img</span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <Link
                        to={`/parts/${part.id}`}
                        className="text-gray-900 hover:text-gray-600 hover:underline"
                      >
                        {part.description}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(part.price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {part.quantity_in_stock}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {part.reorder_threshold}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          part.needs_reorder
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {part.needs_reorder ? 'Reorder' : 'OK'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {part.vendor_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <Link
                        to={`/parts/${part.id}/edit`}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() => deletePart(part.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-4 text-sm text-gray-600">
        Showing {sortedParts.length} part{sortedParts.length !== 1 ? 's' : ''}
      </div>
    </div>
  )
}
